import
{
    useL10n
} from "@ews/react-localization-context";

import
{
    AuthorizationLevel,
    Event,
    FilterCategory,
    addressTypeFuse,
    deviceType,
    eventDetails,
    faultsDetails,
    formatLogicalAddress,
    getLogicalAddress,
    getMemberNumber,
    ioText,
    isValidDeviceNumber,
    loopType,
    slotIdentifier,
    timestampToDate
} from "@ews/zlt-events";

import
{
    IonButton,
    IonCheckbox,
    IonCol,
    IonGrid,
    IonLabel,
    IonRow
} from "@ionic/react";

import
{
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";

import FilterList, { isFilterActive } from "../../../FilterList/FilterList";

import List from "../../../List/List";

import
{
    defaultPageRange
} from "../../../List/Pagination/pagerange";

import
{
    PaginatedResult,
    useFetchSystemEvents
} from "../../../ReactData/system";

import
{
    useIsMobile
} from "../../../Responsive";

import DatePicker, {
    dateRange,
    dateStringToValue,
    valueToDateString
} from "../../../Settings/SettingsComponents/SettingsRangeInput/components/DatePicker/DatePicker";

import SettingsZoneRangeInput, { covertZoneStringToObject } from "../../../Settings/SettingsComponents/SettingsRangeInput/components/Zone/SettingsZoneRangeInput";

import
{
    Filter
} from "../types/reportTypes";

import
{
    allEventsKindType,
    filterEventList
} from "./helper";

import
{
    EventFilterKeys,
    EventMemoryProps
} from "./types";

import
{
    PaginationRequest,
    permissionAllowed,
    RpcSendListRequestExportableList,
    SystemEvents,
    SystemType
} from "@ews/react-data";

import
{
    useAuthorization
} from "../../../Authorization";

import
{
    useMyDateFormat,
    useMyPermissions
} from "../../../Login/LoginProvider";

import { Command, getTranslationKey } from "@ews/zlt-operate";
import { FilterState } from "../../../FilterList/types";
import { usePersistentState } from "../../../Persistence";
import { useInvokeSendList } from "../../../ReactData/rpc";
import eventStyle from "./EventMemory.module.scss";
import { HeaderOption } from "../../../List/types";
import { Locale } from "../../../../setup/locale";

type PersistenceFilter = Filter<EventFilterKeys> & FilterState;
//const ignoreKeys: (keyof PersistenceFilter)[] = ["pageSize", "isFilterOpen", "page", "pageSize", "sortCriteria", "sortOrder"];


const defaultRange = dateRange(new Date(), 1);


const standardFilter: Filter<EventFilterKeys, keyof SystemEvents> = {
    "eventDetails.filter": allEventsKindType,
    timestamp: valueToDateString(defaultRange),
    sortCriteria: "timestamp,runningNumber" as keyof SystemEvents,
    sortOrder: "DESC",
    zone: "1/001,65535/255",
    pageSize: defaultPageRange
};

const EventMemory: React.FC<EventMemoryProps> = ({
    system,
    filter,
    onIsOpen,
    onFilterActive
}) =>
{
    const invokeSendList = useInvokeSendList();
    const { translate: t } = useL10n();
    const dateFormat = useMyDateFormat();
    const permissions = useMyPermissions();

    const filterData = useFetchSystemEvents();
    const mobileView = useIsMobile();


    //const [clearFilterInputs/*, setClearFilterInputs*/] = useState(false);

    //const filterDetailsOpen = false;
    //const [hideDetailsFilter, setHideDetailsFilter] = useState(true);

    const isBC216 = useMemo(() => system.systemType === SystemType.BC216, [system.systemType]);


    const [result, setResult] = useState<PaginatedResult<SystemEvents>>({
        currentPage: 0,
        pageSize: standardFilter.pageSize || defaultPageRange,
        totalCountOfEntries: 0,
        totalCountOfPages: 0,
        results: [],
    });

    const [currentState, setState] = usePersistentState<PersistenceFilter>('systems-event-memory:filter', { ...standardFilter } as PersistenceFilter);
    const sortCriteria = useRef<{
        key: string;
        direction: "ASC" | "DESC";
    } | undefined>(undefined);


    const onPageChange = useCallback((page: PersistenceFilter["page"]) => setState((prev) => ({ ...prev, page })), [setState]);
    const onSortChange = useCallback((sortCriteria: PersistenceFilter["sortCriteria"], sortOrder: PersistenceFilter["sortOrder"]) => setState((prev) => ({ ...prev, sortCriteria, sortOrder })), [setState]);
    const onPageSizeChange = useCallback((pageSize: PersistenceFilter["pageSize"]) => setState((prev) => ({ ...prev, pageSize })), [setState]);
    const onReloadChange = useCallback(() => setState((prev) => ({ ...prev })), [setState]);


    const onIsFilterOpen = useCallback((state: boolean) => { onIsOpen && onIsOpen(state, "main"); }, [onIsOpen]);
    //const onIsDetailFilterOpen = useCallback((isDetailOpen: boolean) => onIsOpen && onIsOpen(isDetailOpen, "detail"), [onIsOpen]);
    const onResetFilter = useCallback(() => setState(() => standardFilter), [setState]);

    const isFilterChanged = isFilterActive(currentState, standardFilter, []);



    useEffect(() =>
    {
        if (filter?.isFilterActive !== isFilterChanged) {
            onFilterActive && onFilterActive(isFilterChanged);
        }
    }, [isFilterChanged, filter, onFilterActive]);

    const handleSelectEvent = useCallback((eventKindType: number, checked: boolean) =>
    {
        const selectedEvent = currentState["eventDetails.filter"] ? currentState["eventDetails.filter"] : 0b00;

        const newState = { ...currentState };

        if (eventKindType === 0) delete newState!["eventDetails.filter"];
        else newState!["eventDetails.filter"] = checked ? eventKindType | selectedEvent : eventKindType ^ selectedEvent;

        //delete currentFilter.page;

        setState({ ...newState });
    }, [currentState, setState]);

    // function resetFilter()
    // {
    //     numberOfItemsPerPage.current = pageRange[1];
    //     setFilterEvents(standardFilter);
    // }

    const getFilteredData = useCallback(async (id: string, filter: PaginationRequest) =>
    {
        const response = await filterData(id, filter);
        if (!response) return;

        setResult(response);
    }, [filterData]);

    useEffect(() =>
    {
        if (system.id) getFilteredData(system.id, currentState);
    }, [currentState, system, getFilteredData]);


    const onClickSendList = useCallback(() =>
    {
        if (!system?.id) {
            console.error("onClickSendList: system.id is undefined. This should not be possible. Calling sendList rpc skipped.");
            return;
        }

        console.debug("onClickSendList: invoking sendList rpc with filter", currentState);

        invokeSendList({
            apiParams: {
                systemId: system.id
            } as any,
            filter: currentState as any,
            list: RpcSendListRequestExportableList.SystemEvents,
        });
    }, [currentState, invokeSendList, system.id]);

    const header: HeaderOption<Locale, SystemEvents>[] = useMemo(() => [
        {
            title: 'TD_ID',
            key: 'runningNumber',
            breakpoint: 1200,
            up: true,
            className: `${eventStyle["col"]} ${mobileView ? eventStyle["sizeFull"] : eventStyle["size1"]}`
        },
        //{ title: 'eventKindType', key: 'eventKindType', both: true },// nur zum Testen
        {
            title: 'TD_ZEIT',
            className: `${eventStyle["col"]} ${mobileView ? eventStyle["sizeFull"] : eventStyle["size3"]}`,
            key: 'timestamp,runningNumber',
            both: true,
            child: ({ created, command, timestamp }: SystemEvents) =>
            {
                return timestamp ? <>
                    {/* formatDate(command ? new Date(created) : timestampToDate(timestamp), { timeZone: 'UTC', dateStyle: "medium", timeStyle: "medium" }) */}
                    {dateFormat(timestampToDate(timestamp), "UTC")}
                </> : null;
            }
        },
        {
            title: 'TD_ELEM2',
            className: `${eventStyle["col"]} ${mobileView ? eventStyle["sizeFull"] : eventStyle["size2"]}`,
            key: 'zone,element',
            both: true,
            child: (event: SystemEvents) =>
            {
                const { command, numberGroup, firstElementOfRange, firstZoneOfRange, lastElementOfRange, lastZoneOfRange, panelNumber } = event;

                if (command && firstZoneOfRange && lastZoneOfRange && firstElementOfRange && lastElementOfRange) {
                    const from = formatLogicalAddress(firstZoneOfRange!, firstElementOfRange, numberGroup || 0);
                    const to = formatLogicalAddress(lastZoneOfRange!, lastElementOfRange, numberGroup || 0);
                    return <>
                        {from && <>{from}{!isBC216 && <> - {to}</>}</>}
                    </>;
                }

                if (isValidDeviceNumber(panelNumber!)) return <>{getLogicalAddress(event as unknown as Event)}</>;
                return <></>;

            }
        },
        {
            title:
                '[SID_MNU_HK_ONE_EVENT]', className:
                `${eventStyle["col"]} ${mobileView ? eventStyle["sizeFull"] :
                    eventStyle["size4"]}`, key:
                'eventKindType', both:
                true, child:
                ({ command, userUserName, eventKindType, softwareDeviceType }: SystemEvents) =>
                {

                    return <div>
                        {command &&
                            <>
                                {userUserName ? `[${userUserName}]` : t("TD_BEDIENHANDLUNG")}<span> &#8680; </span>
                            </>
                        }
                        {eventKindType && <>{t(eventDetails(eventKindType, softwareDeviceType || 0)?.translationKey || "")}</>}
                        {(command && !eventKindType) && <>{t(getTranslationKey(command as Command))}</>}
                    </div>;
                }
        },
        //{ title: 'COM_FACPMANAGEMENT_LOGS_EVENTS_MSG_DETAILS', key: 'detailedText', breakpoint: 1200, up: true, localeCompare(b['detailedText']) },
        { title: 'TD_TEXT_1', key: 'groupText', both: true, className: `${eventStyle["col"]}` },
        { title: 'TD_TEXT_2', key: 'elementText', both: true, className: `${eventStyle["col"]}` },
        ...(permissionAllowed(permissions.permitAccessLevel2!) ? [
            {
                title: 'COM_FACPMANAGEMENT_LOGS_EVENTS_PANEL',
                className: `${eventStyle["col"]} ${mobileView ? eventStyle["sizeFull"] : eventStyle["size1"]}`,
                key: 'panelNumber',
                both: (true as true),
                child: ({ addressType, mainAddress, panelNumber, panelName }: { addressType: number, mainAddress: number, panelNumber: number, panelName: string; }) =>
                {
                    return <>
                        {mobileView && t('TLN:')} {getMemberNumber({
                            addressType, mainAddress, panelNumber
                        } as Event)}{panelNumber === 1 && <>{panelName && ', '}{panelName}</>}
                    </>;
                }
            }] : []),
        ...(permissionAllowed(permissions.permitAccessLevel3!) ? [
            {
                title: 'COM_FACPMANAGEMENT_LOGS_EVENTS_DETAILS',
                className: `${eventStyle["col"]} ${mobileView ? eventStyle["sizeFull"] : eventStyle["size3"]}`,
                key: 'details',
                both: (true as true),
                child: ({
                    backplaneNumber,
                    busIdentifier,
                    softwareDeviceType,
                    loopElement,
                    posLoopElement,
                    mainAddress,
                    subAddress,
                    addressType,
                    errorCode,
                    measurement
                }: SystemEvents) =>
                {

                    const slotNumber = slotIdentifier(backplaneNumber!, busIdentifier!);
                    const errorText = faultsDetails(errorCode || 0) || {};

                    return <><div>
                        {/* !!softwareDeviceType so we don't end up with literal 0 for softwareDeviceType === 0 */}
                        {slotNumber} {!!softwareDeviceType && <>{t(deviceType(softwareDeviceType!))}-x</>}
                    </div>
                        <div>
                            {!(addressType) ? <>
                                {isValidDeviceNumber(posLoopElement!) && <>
                                    {t('[SID_DETAILS_POS]')}: {posLoopElement}{isValidDeviceNumber(mainAddress!) && <>,<span> </span>
                                        {t('[SID_DETAILS_ADDR]')}: {mainAddress}{isValidDeviceNumber(subAddress!) && <>,<span> </span>
                                            {t('[SID_DETAILS_SUB]')}: {subAddress}
                                        </>}
                                    </>}
                                </>}
                            </> : <>
                                {(softwareDeviceType && isValidDeviceNumber(addressType || 0)) &&
                                    <>
                                        <>{t(ioText(softwareDeviceType!, addressType!, mainAddress!) || "")}</>
                                        <>{addressTypeFuse(addressType) && `${t('[SID_DETAILS_FUSE]')}${mainAddress}`}</>
                                    </>
                                }
                            </>
                            }
                        </div>
                        <div>
                            {isValidDeviceNumber(loopElement || 0) && t(loopType(loopElement!))}
                        </div>
                        <div>
                            {errorText.translationKey && <>{t(errorText.translationKey || "")}</>}
                        </div>
                        <div>
                            {isValidDeviceNumber(measurement!) && <>{t('COM_FACPMANAGEMENT_LOGS_MEASUREMENTS_ALARM_VALUE')}: {measurement}</>}
                        </div>
                    </>;
                }
            },
        ] : []) as any
    ], [
        permissions.permitAccessLevel2,
        permissions.permitAccessLevel3,
        dateFormat,
        isBC216,
        mobileView,
        t
    ]);

    useEffect(() =>
    {
        sortCriteria.current =
            currentState.sortCriteria ? {
                key: currentState.sortCriteria,
                direction: currentState.sortOrder || "ASC"
            } : undefined;

    }, [currentState.sortCriteria, currentState.sortOrder]);

    return <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <FilterList
            onReset={onResetFilter}
            //onDetailOpen={onIsDetailFilterOpen}
            onIsOpen={onIsFilterOpen}
            isOpen={filter?.isFilterOpen || false}
            noPrint
        >
            <IonCol>
                <IonGrid style={{ overflowY: "auto" }}>
                    <IonRow>
                        <IonCol style={{ border: "0" }}>
                            <div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
                                <IonLabel style={{ maxWidth: "fit-content" }}>{t("TD_FILTER")}</IonLabel>
                            </div>
                        </IonCol>
                    </IonRow>
                    <IonRow>

                        {filterEventList.map((eventKindType, index) =>
                        {

                            let isChecked = false;
                            if (eventKindType !== allEventsKindType && currentState["eventDetails.filter"]) {
                                let byte = eventKindType & currentState["eventDetails.filter"];
                                isChecked = Boolean(byte);
                            }
                            else {
                                isChecked = currentState["eventDetails.filter"] === allEventsKindType;
                            }
                            const width = mobileView ? { minWidth: "100%" } : { width: "fit-content" };

                            return (
                                <IonCol
                                    sizeLg="4"
                                    sizeMd="1"
                                    sizeSm="6"
                                    style={{ display: "flex", justifyContent: "start", alignItem: "center", border: "0", paddingStart: index === 0 ? "5px" : "", ...width }}
                                    key={`${index}`} >

                                    <IonCheckbox
                                        style={{ margin: 0, padding: 0, width: "fit-content" }}
                                        checked={isChecked}
                                        onIonChange={({ detail }) =>
                                        {
                                            handleSelectEvent(eventKindType, detail.checked);
                                        }}>
                                        {" "}
                                    </IonCheckbox>
                                    <IonLabel style={{ margin: "0 10px", verticalAlign: "middle" }}>{eventKindType === allEventsKindType ? t("[REACT_FC_ALL]") : t(`[REACT_FC_${FilterCategory[eventKindType].toLocaleUpperCase()}]`)}</IonLabel>
                                </IonCol>
                            );
                        }
                        )}
                    </IonRow>
                </IonGrid>
                <IonCol style={{ border: "0" }} ></IonCol>
            </IonCol>
            <IonCol sizeLg="3" sizeMd="1" sizeSm="1">
                <div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
                    <IonLabel style={{ maxWidth: "fit-content" }}>{t("TD_DATUM_DRUCK")}</IonLabel>
                </div>
                <DatePicker
                    className={eventStyle.dateTime}
                    childText={{ start: "[REACT_START]", end: "[REACT_END]" }}
                    value={dateStringToValue(currentState.timestamp || "")}
                    divider={false}
                    position="row"
                    onChange={(range) =>
                    {

                        const currentFilter = { ...currentState };
                        currentFilter.timestamp = valueToDateString(range);
                        console.log("currentFilter.created: ", currentFilter.timestamp);
                        setState({ ...currentFilter });
                    }}

                />
            </IonCol>

            <IonCol sizeLg="3" sizeMd="1" sizeSm="1">
                <div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
                    <IonLabel style={{ maxWidth: "fit-content" }}>{t("[SID_ELEMENT_DEV]")}</IonLabel>
                </div>
                <SettingsZoneRangeInput
                    lines="none"
                    divider={false}
                    value={covertZoneStringToObject(currentState.zone) || {}}
                    labels={{ start: "[SID_FROM]", end: "[SID_TO]" }}
                    inputLabels={{ parent: "[REACT_NUMBER]", children: "[SID_ELEMENT_DEV]" }}
                    position={{ parent: "row", children: "line" }}
                    reset={false}
                    onChange={(value) =>
                    {
                        const currentFilter = { ...currentState };

                        currentFilter.zone = `${value.startZoneElement}`;

                        if (value.endZoneElement) {
                            currentFilter.zone = `${value.startZoneElement},${value.endZoneElement}`;
                        }

                        setState({ ...currentFilter });
                    }} />

            </IonCol>
            <IonCol style={{ display: 'flex', flexDirection: "column", alignItems: "end" }}>
                <IonRow >
                    <IonButton
                        color={"success"}
                        onClick={onClickSendList}
                    >
                        <span>{t("[REACT_WERTE_VERSENDEN]")}</span>
                    </IonButton>
                </IonRow>
            </IonCol>
        </FilterList >


        <List
            rows={result.results}
            sort={sortCriteria.current}
            onSort={onSortChange}
            onPageChange={onPageChange}

            pageSize={result.pageSize}
            onPageSize={onPageSizeChange}
            onReload={onReloadChange}

            currentPage={result.currentPage}
            numberOfPages={result.totalCountOfPages}
            maxHeight={mobileView ? "86vh" : "47vh"}
            noLink
            //link={{ url: "system:events", paramsKey: "id" }}
            headers={header}
        />
    </div>;
};

export default EventMemory;
